import { Params, Routes } from '@angular/router';

import { ActivityCoverStore } from './activity-edit/stores/activity-cover.store';
import { ActivityEditStore } from './activity-edit/stores/activity-edit.store';
import { ActivityProjectContextStore } from './activity-edit/stores/activity-project-context.store';
import { ActivityStickerStore } from './activity-edit/stores/activity-sticker.store';
import { DOCUMENT_LIBRARY_ROUTE_PROVIDERS } from './document-library/document-library-route.providers';
import { DOCUMENT_LIBRARY_ROUTE_PATH } from './document-library/document-library.routes';
import { activityEditCanDeactivateGuard } from './guards/activity-edit-can-deactivate.guard';
import { leckeAdminGuard } from './guards/lecke-admin.guard';
import { profileGuard } from './guards/profile.guard';
import { KT_TRANSLOCO } from './kt.transloco';
import { PROJECTS_ROUTE_PROVIDERS } from './projects/projects-route.providers';
import { PROJECTS_ROUTE_CONST } from './projects/projects.routes';
import { PROJECTS_TRANSLOCO } from './projects/projects.transloco';
import { USER_MANAGEMENT_ROUTE_PROVIDERS } from './user-management/user-management-route.providers';
import { USER_MANAGEMENT_ROUTE_PATH } from './user-management/user-management.routes';

export { ktProjectDetailRoute } from './projects/projects.routes';
export { USER_MANAGEMENT_LIST_ROUTE as KT_USER_MANAGEMENT_ROUTE } from './user-management/user-management.routes';

export const KT_ROUTES_CONST = {
  HOME: '',
  GUIDED_ACTIVITY_WIZARD: 'guided-activity-wizard',
  ACTIVITY_EDIT: 'activity/:id/edit',
  PROJECTS: PROJECTS_ROUTE_CONST,
  CALENDAR: 'calendar',
  DOCUMENT_LIBRARY: DOCUMENT_LIBRARY_ROUTE_PATH,
  USER_MANAGEMENT: USER_MANAGEMENT_ROUTE_PATH,
};

/** Router link to the activity library home (`KT_ROUTES_CONST.HOME` is `''`, so this is root `/`). */
export const KT_HOME_ROUTE = ['/'] as const;

/** Router link to the projects list page — derived from `KT_ROUTES_CONST.PROJECTS`. */
export const KT_PROJECTS_ROUTE = ['/', KT_ROUTES_CONST.PROJECTS] as const;

/** Router link to the calendar page — derived from `KT_ROUTES_CONST.CALENDAR`. */
export const KT_CALENDAR_ROUTE = ['/', KT_ROUTES_CONST.CALENDAR] as const;

/** Router link to the document library page. */
export const KT_DOCUMENT_LIBRARY_ROUTE = ['/', KT_ROUTES_CONST.DOCUMENT_LIBRARY] as const;

/**
 * Router commands to the dedicated edit page (`activity/:id/edit`) for a given activity id.
 *
 * @param activityId - The activity instance id to edit.
 * @returns The `Router.navigate` commands array for the edit route.
 */
export function activityEditRoute(activityId: string): string[] {
  // Derive the navigate commands from the single route-path source, so a change to
  // `ACTIVITY_EDIT` (e.g. renaming the segment) can't silently drift from this helper.
  const [first, ...rest] = KT_ROUTES_CONST.ACTIVITY_EDIT.split('/');

  return [`/${first}`, ...rest.map((segment) => (segment === ':id' ? activityId : segment))];
}

export const ACTIVITY_EDIT_PROJECT_ID_QUERY_PARAM = 'projectId';

/**
 * Router commands and query params for the dedicated edit page, carrying the open project
 * context as `?projectId=` when one is given (KT-198.2 / D1).
 *
 * @param activityId - The activity instance id to edit.
 * @param projectId - The open project to carry over; omitted / `null` / empty means none.
 * @returns The `Router.navigate` commands and query params — the latter empty (no query string)
 * when there is no project context, so callers can always spread it into their navigation extras.
 */
export function activityEditNavigation(
  activityId: string,
  projectId?: string | null,
): { commands: string[]; queryParams: Params } {
  const hasProject = projectId !== undefined && projectId !== null && projectId.length > 0;

  return {
    commands: activityEditRoute(activityId),
    queryParams: hasProject ? { [ACTIVITY_EDIT_PROJECT_ID_QUERY_PARAM]: projectId } : {},
  };
}

const KT_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () => import('./layout/kt-layout/kt-layout'),
    children: [
      {
        path: KT_ROUTES_CONST.HOME,
        loadComponent: () => import('./pages/home/home'),
        providers: [KT_TRANSLOCO.provider],
      },
      {
        path: KT_ROUTES_CONST.GUIDED_ACTIVITY_WIZARD,
        loadComponent: () =>
          import('./guided-activity-wizard/pages/guided-activity-create-page/guided-activity-create-page'),
        providers: [KT_TRANSLOCO.provider],
        // FE-4 (option D): profile-less teachers are bounced back to `/` (home overlay-host);
        // home itself stays unguarded so FUE/profile overlays can always render there.
        canActivate: [profileGuard],
      },
      {
        path: KT_ROUTES_CONST.PROJECTS,
        // Missing teacher profile → redirect to `/` (S32). Auth pipeline handles 401.
        canActivate: [profileGuard],
        providers: PROJECTS_ROUTE_PROVIDERS,
        children: [
          {
            path: '',
            loadComponent: () => import('./projects/pages/projects-list-page/projects-list-page'),
          },
          {
            path: ':projectId',
            loadComponent: () => import('./projects/pages/project-detail-page/project-detail-page'),
          },
        ],
      },
      {
        path: KT_ROUTES_CONST.CALENDAR,
        // Missing teacher profile → redirect to `/` (same as projects).
        canActivate: [profileGuard],
        providers: [KT_TRANSLOCO.provider],
        loadComponent: () => import('./calendar/pages/calendar-page/calendar-page'),
      },
      {
        path: KT_ROUTES_CONST.DOCUMENT_LIBRARY,
        canActivate: [profileGuard],
        providers: DOCUMENT_LIBRARY_ROUTE_PROVIDERS,
        loadComponent: () => import('./document-library/pages/document-library-list-page/document-library-list-page'),
      },
      {
        path: KT_ROUTES_CONST.USER_MANAGEMENT,
        canActivate: [profileGuard, leckeAdminGuard],
        providers: USER_MANAGEMENT_ROUTE_PROVIDERS,
        loadComponent: () => import('./user-management/pages/user-management-page/user-management-page'),
      },
      {
        path: KT_ROUTES_CONST.ACTIVITY_EDIT,
        loadComponent: () => import('./activity-edit/pages/activity-edit-page/activity-edit-page'),
        // Route-scoped editor stores: they live as long as this route's injector, which can outlive
        // the page.
        providers: [
          KT_TRANSLOCO.provider,
          PROJECTS_TRANSLOCO.provider,
          ActivityEditStore,
          ActivityProjectContextStore,
          ActivityStickerStore,
          ActivityCoverStore,
        ],
        // Profile-less teachers are bounced back to `/` (home overlay-host) — S29.
        canActivate: [profileGuard],
        // Flush a still-pending valid autosave before the route is left, so a change sitting in
        // the debounce window is not lost silently. Blocks only while a sticker generation runs and the
        // teacher chooses to stay.
        canDeactivate: [activityEditCanDeactivateGuard],
      },
    ],
  },
];

export default KT_ROUTES;
